"use client"

import React from "react"
import Link from "next/link"
import { useParams, usePathname } from "next/navigation"
import { SharedIntakeForm, User } from "@/schema"
import { useQueries } from "@tanstack/react-query"
import { BreadcrumbItem, BreadcrumbList, BreadcrumbSeparator, Breadcrumb as BreadcrumbUI, Skeleton } from "@ui/shad"

import { isAllBooksId } from "@/lib/all-books"
import { BreadcrumbResponse, fetchBreadcrumb } from "@/hooks/api/breadcrumbs/api"
import { fetchSharedIntakeForm } from "@/hooks/api/intake-forms"
import { useDashboard } from "@/hooks/api/organizations/queries"
import { useApiContext } from "@/hooks/api/use-api-context"
import { fetchCurrentUser } from "@/hooks/api/users/api"
import { useDevAuth as useAuth } from "@/hooks/use-dev-auth"

const BOOK_ID_PATTERN = /^\/[0-9a-zA-Z]{10}$/i

const DYNAMIC_SEGMENT_TYPES: string[] = [
  "clients",
  "households",
  "contacts",
  "meetings",
  "prospects",
  "past-clients",
  "projects",
  "service-requests",
  "reports",
  "sequences",
  "scouting",
  "businesses",
  "trusts",
  "people",
  "automations",
  "integration-requests",
  "intake-forms",
  "invitations",
  "requests",
  "submissions",
]

const NESTED_RESOURCE_SEGMENTS = ["invitations", "requests", "submissions"]

const NEW_RESOURCE_LABELS: Record<string, string> = {
  "intake-forms": "New intake form",
  requests: "New request",
  "service-requests": "New request",
}

// Single breadcrumb labels for specific nested routes (collapses parent + child into one label)
const SINGLE_BREADCRUMB_ROUTES: Record<string, string> = {
  "clients/people": "Client people view",
  "clients/reviews": "Client reviews",
  "clients/rmds": "RMDs",
  "schwab-activity": "Schwab activity",
}
const SEGMENT_LABELS: Record<string, string> = { slanta: "Slanta Claus" }
type DATA_RESULT_TYPES = User | BreadcrumbResponse | SharedIntakeForm | null

function capitalizeWords(str: string | null | undefined): string {
  if (!str) return ""
  if (SEGMENT_LABELS[str]) return SEGMENT_LABELS[str]
  return str
    .replace(/-/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")
}

type DynamicSegment = {
  type: string
  id: string
  index: number
}

export default function BreadcrumbNav() {
  const pathname = usePathname()
  const { organizationId, dashboardId } = useParams<{ organizationId?: string; dashboardId?: string }>()
  const dashboard = useDashboard(organizationId ?? "", dashboardId ?? "")
  const getContext = useApiContext()
  const { userId } = useAuth()
  const pathParts = pathname.split("/").filter(Boolean)

  const dynamicSegments = pathParts.reduce<DynamicSegment[]>((acc, part, index) => {
    if (index === 0 && BOOK_ID_PATTERN.test(`/${part}`)) {
      acc.push({ type: "bookId", id: part, index })
      return acc
    }

    const previousPart = pathParts[index - 1]

    if (previousPart === "shared" && pathParts[index - 2] === "intake-forms") {
      acc.push({ type: "sharedIntakeForm", id: part, index })
      return acc
    }

    if (
      DYNAMIC_SEGMENT_TYPES.includes(previousPart as string) &&
      part !== "templates" &&
      part !== "new" &&
      part !== "shared" &&
      !NESTED_RESOURCE_SEGMENTS.includes(part)
    ) {
      acc.push({ type: previousPart as string, id: part, index })
    }

    return acc
  }, [])

  const queryConfigs = dynamicSegments.map((segment) => {
    if (segment.type === "bookId") {
      return {
        queryKey: ["user", "me", userId],
        queryFn: async () => {
          const context = await getContext()
          if (!context) throw new Error("No context found")
          return fetchCurrentUser(context)
        },
        staleTime: 1000 * 60 * 10,
      }
    } else if (segment.type === "sharedIntakeForm") {
      return {
        queryKey: ["shared_intake_form", segment.id],
        queryFn: async () => fetchSharedIntakeForm(await getContext(), segment.id),
      }
    } else {
      return {
        queryKey: ["breadcrumb", segment.type, segment.id],
        queryFn: async () => {
          const context = await getContext()
          if (!context) throw new Error("No context found")
          return fetchBreadcrumb(context, segment.type, segment.id)
        },
        meta: { id: segment.id, type: segment.type },
      }
    }
  })

  const queryResults = useQueries({ queries: queryConfigs })

  const resultsMap = new Map<string, { data: DATA_RESULT_TYPES | null; isLoading: boolean }>()
  queryResults.forEach((result, index) => {
    const segment = dynamicSegments[index]
    resultsMap.set(`${segment.type}-${segment.id}`, {
      data: result.data ?? null,
      isLoading: result.isLoading,
    })
  })

  const breadcrumbs = pathParts
    .map((part, index) => {
      const currentPath = `/${pathParts.slice(0, index + 1).join("/")}`
      if (part === "account-openings") return null
      if (pathParts[index - 1] === "account-openings") {
        return { label: "Account opening", href: currentPath, isLoading: false }
      }
      if (dashboardId && part === dashboardId && pathParts[index - 1] === "dashboards") {
        return {
          label: dashboard.data?.name ?? "Dashboard",
          href: currentPath,
          isLoading: dashboard.isLoading,
        }
      }
      if (pathParts[1] === "audit-log" && index > 1) {
        const labels: Record<string, string> = {
          household: "Household",
          project: "Project",
          task: "Task",
          opportunity: "Opportunity",
          trust: "Trust",
        }
        return index === pathParts.length - 1
          ? { label: labels[pathParts[2]] ?? "Audit log", href: currentPath, isLoading: false }
          : null
      }

      if (index > 0) {
        const previousPart = pathParts[index - 1]
        const routeKey = `${previousPart}/${part}`
        if (SINGLE_BREADCRUMB_ROUTES[routeKey]) {
          return { label: SINGLE_BREADCRUMB_ROUTES[routeKey], href: currentPath, isLoading: false }
        }
        const nextPart = pathParts[index + 1]
        if (nextPart && SINGLE_BREADCRUMB_ROUTES[`${part}/${nextPart}`]) {
          return null
        }
      }

      const dynamicSegment = dynamicSegments.find((segment) => segment.index === index)
      if (dynamicSegment) {
        const query = resultsMap.get(`${dynamicSegment.type}-${dynamicSegment.id}`)

        if (query) {
          const { data, isLoading } = query
          let label = part

          if (data) {
            if (dynamicSegment.type === "bookId") {
              const user = data as User

              if (isAllBooksId(dynamicSegment.id)) {
                label = "All books"
              } else {
                const book = user.books.find((book) => book.id === dynamicSegment.id) ?? user.principal_books?.[0]
                if (book) label = book.name
              }
            } else if (dynamicSegment.type === "sharedIntakeForm") {
              label = (data as SharedIntakeForm).name
            } else {
              label = (data as BreadcrumbResponse).name
            }
          }

          let href = currentPath
          if (dynamicSegment.type === "trusts") {
            const parentIndex = pathParts.findIndex(
              (p, idx) =>
                idx < index && (p === "households" || p === "clients" || p === "prospects" || p === "past-clients")
            )
            if (parentIndex !== -1) {
              const parentSegment = pathParts[parentIndex]
              const parentId = pathParts[parentIndex + 1]
              if (parentId) {
                href = `/${pathParts[0]}/${parentSegment}/${parentId}?secondary=records`
              }
            }
          }

          return {
            label,
            href,
            isLoading,
          }
        }
      }

      // For the "meetings" segment, link to the client's meetings tab
      if (part === "meetings" && index > 0) {
        // Find the client ID in the path
        const clientIndex = pathParts.findIndex((p) => p === "clients")
        if (clientIndex !== -1 && clientIndex < index) {
          const clientId = pathParts[clientIndex + 1]
          return {
            label: capitalizeWords(part),
            href: `/${pathParts[0]}/clients/${clientId}?tab=meetings`,
            isLoading: false,
          }
        }

        // Find the contact ID in the path
        const contactIndex = pathParts.findIndex((p) => p === "contacts")
        if (contactIndex !== -1 && contactIndex < index) {
          const contactId = pathParts[contactIndex + 1]
          return {
            label: capitalizeWords(part),
            href: `/${pathParts[0]}/contacts/${contactId}?tab=meetings`,
            isLoading: false,
          }
        }
      }

      if (part === "trusts" && index > 0) {
        const parentIndex = pathParts.findIndex(
          (p, idx) =>
            idx < index && (p === "households" || p === "clients" || p === "prospects" || p === "past-clients")
        )
        if (parentIndex !== -1) {
          const parentSegment = pathParts[parentIndex]
          const parentId = pathParts[parentIndex + 1]
          if (parentId) {
            return {
              label: capitalizeWords(part),
              href: `/${pathParts[0]}/${parentSegment}/${parentId}?secondary=records`,
              isLoading: false,
            }
          }
        }
      }

      if (part === "people" && index > 0) {
        const parentIndex = pathParts.findIndex(
          (p, idx) =>
            idx < index && (p === "households" || p === "clients" || p === "prospects" || p === "past-clients")
        )
        if (parentIndex !== -1) {
          const parentSegment = pathParts[parentIndex]
          const parentId = pathParts[parentIndex + 1]
          if (parentId) {
            return {
              label: capitalizeWords(part),
              href: `/${pathParts[0]}/${parentSegment}/${parentId}?secondary=details`,
              isLoading: false,
            }
          }
        }
      }

      if (NESTED_RESOURCE_SEGMENTS.includes(part) && pathParts[index - 1] === "intake-forms") {
        return {
          label: capitalizeWords(part),
          href: `/${pathParts[0]}/intake-forms?tab=${part}`,
          isLoading: false,
        }
      }

      if (part === "shared" && pathParts[index - 1] === "intake-forms") {
        return {
          label: "Shared",
          href: `/${pathParts[0]}/intake-forms`,
          isLoading: false,
        }
      }

      // Handle "new" segments with a friendly label based on the parent resource type
      if (part === "new" && index > 0) {
        const parentPart = pathParts[index - 1]
        if (DYNAMIC_SEGMENT_TYPES.includes(parentPart)) {
          const explicitLabel = NEW_RESOURCE_LABELS[parentPart]
          if (explicitLabel) return { label: explicitLabel, href: currentPath, isLoading: false }

          const singularName = parentPart.endsWith("s") ? parentPart.slice(0, -1) : parentPart
          return { label: `New ${singularName}`, href: currentPath, isLoading: false }
        }
      }

      return { label: capitalizeWords(part), href: currentPath, isLoading: false }
    })
    .filter((breadcrumb): breadcrumb is { label: string; href: string; isLoading: boolean } => breadcrumb !== null)

  return (
    <BreadcrumbUI>
      <BreadcrumbList>
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={`${crumb.href}-${index}`}>
            <BreadcrumbItem>
              {crumb.isLoading ? (
                <Skeleton className="h-4 w-32" />
              ) : index === breadcrumbs.length - 1 ? (
                <span className="text-muted-foreground">{crumb.label}</span>
              ) : (
                <Link href={crumb.href}>{crumb.label}</Link>
              )}
            </BreadcrumbItem>
            {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </BreadcrumbUI>
  )
}
